import { buildInitialOptionState, updateOptionCounts } from "./filters/optionCounts";
import { renderTags } from "./filters/tags";

const DATA_KEYS = ["bundesland", "schulform", "fach", "jahrgangsstufe", "inhalt", "lehrwerk"];

/**
 * Initialisiert die Filterlogik, erstellt den Token-Cache und reagiert auf Änderungen
 * an Filtern und Sortierung.
 */
export function initFilters() {
    const filters = document.querySelectorAll(".filter");
    const items = Array.from(document.querySelectorAll("#items .item"));
    const activeFiltersEl = document.getElementById("active-filters");

    const sortSelect = document.querySelector('select[name="sort"]');

    if (!items.length || !activeFiltersEl) return;

    const tokenCache = buildTokenCache(items);
    const initialOptionState = buildInitialOptionState(filters, items, tokenCache);

    document.addEventListener("change", () => {
        applyFilters(filters, items, tokenCache, sortSelect, initialOptionState);
    });

    applyFilters(filters, items, tokenCache, sortSelect, initialOptionState);
}

/**
 * Erstellt einen Cache mit vorbereiteten Tokens, Datumswerten und Headline-Texten
 * für jedes Item, damit Filter- und Sortieroperationen effizient ausgeführt werden können.
 */
function buildTokenCache(items) {
    const cache = new Map();

    items.forEach((el) => {
        const tokens = {};

        DATA_KEYS.forEach((key) => {
            const raw = (el.dataset[key] || "").trim();
            tokens[key] = raw ? new Set(raw.split(/\s+/)) : new Set();
        });

        const dateStr = (el.dataset.date || "").trim();
        tokens._dateTs = dateStr ? Date.parse(dateStr) : NaN;

        // Beliebtheit aus data-popular, wird absteigend sortiert.
        const popularStr = (el.dataset.popular || "").trim();
        tokens._popular = popularStr ? Number.parseFloat(popularStr) : NaN;

        // Für alphabetische Sortierung wird ausschließlich die sichtbare Headline verwendet.
        tokens._headlineText = getItemHeadlineText(el);

        cache.set(el, tokens);
    });

    return cache;
}

/**
 * Liefert den Text, der für die alphabetische Sortierung verwendet wird.
 * Bei Weekly-Kacheln ist das ausschließlich der Text aus .weekly__headline.
 */
function getItemHeadlineText(item) {
    return (item.querySelector(".weekly__headline")?.textContent || "")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Liest alle aktuell ausgewählten Checkbox-Filter und die gewählte Sortierung aus
 * und gibt sie als Objekt zurück.
 */
function getActiveFilters(filters, sortSelect) {
    const active = {};

    filters.forEach((filter) => {
        const key = filter.dataset.filter;
        const checked = filter.querySelectorAll("input:checked");

        if (checked.length) {
            active[key] = Array.from(checked).map((input) => input.value);
        }
    });

    if (sortSelect && sortSelect.value) {
        active.sortierung = [sortSelect.value];
    }

    return active;
}

/**
 * Führt den kompletten Filterprozess aus, aktualisiert Optionen,
 * sichtbare Items und aktive Tags.
 */
function applyFilters(filters, items, tokenCache, sortSelect, initialOptionState) {
    const active = getActiveFilters(filters, sortSelect);

    updateOptionCounts(filters, items, active, tokenCache, initialOptionState);
    updateItems(items, active, tokenCache);
    renderTags(active, filters, items);
    updateFilterCounts(filters, active);
}

/**
 * Blendet Items anhand der aktiven Filter ein oder aus und sortiert sie
 * anschließend nach der gewählten Sortierreihenfolge.
 */
function updateItems(items, active, tokenCache) {
    const filterKeys = Object.keys(active).filter((key) => key !== "sortierung");

    items.forEach((item) => {
        const tokens = tokenCache.get(item);
        if (!tokens) return;

        const visible = matchesAllFilters(tokens, active, filterKeys);

        if (visible) {
            if (item.classList.contains("item--hidden")) {
                item.classList.remove("item--hidden");
                item.classList.add("item--hiding");
                requestAnimationFrame(() => item.classList.remove("item--hiding"));
            } else {
                item.classList.remove("item--hiding");
            }
            return;
        }

        if (!item.classList.contains("item--hidden")) {
            item.classList.add("item--hiding");

            window.setTimeout(() => {
                const latestActive = getCurrentActiveFiltersFromDOM();
                const latestKeys = Object.keys(latestActive).filter((key) => key !== "sortierung");
                const nowVisible = matchesAllFilters(tokens, latestActive, latestKeys);

                if (!nowVisible) {
                    item.classList.add("item--hidden");
                }
            }, 230);
        }
    });

    const sort = active.sortierung?.[0];
    if (!sort) return;

    const container = document.getElementById("items");
    if (!container) return;

    [...items]
        .sort((a, b) => {
            if (sort === "date") {
                const ta = tokenCache.get(a)?._dateTs;
                const tb = tokenCache.get(b)?._dateTs;

                return (tb || 0) - (ta || 0);
            }

            if (sort === "popular") {
                const pa = tokenCache.get(a)?._popular;
                const pb = tokenCache.get(b)?._popular;

                // Items ohne gültigen Wert landen am Ende.
                return toSortableNumber(pb) - toSortableNumber(pa);
            }

            if (sort === "alpha") {
                const headlineA = tokenCache.get(a)?._headlineText || "";
                const headlineB = tokenCache.get(b)?._headlineText || "";

                return headlineA.localeCompare(headlineB, "de", {
                    sensitivity: "base",
                    numeric: true,
                    ignorePunctuation: true,
                });
            }

            return 0;
        })
        .forEach((item) => container.appendChild(item));
}

/**
 * Wandelt einen möglicherweise ungültigen Zahlenwert in einen sortierbaren Wert um.
 * Ungültige Werte werden ans Ende der absteigenden Sortierung geschoben.
 */
function toSortableNumber(value) {
    return Number.isFinite(value) ? value : Number.MIN_SAFE_INTEGER;
}

/**
 * Prüft, ob ein Item alle aktiven Filter erfüllt.
 * - innerhalb derselben Gruppe: ODER
 * - zwischen Gruppen: UND
 */
function matchesAllFilters(tokens, active, filterKeys) {
    return filterKeys.every((key) => {
        const set = tokens[key];
        if (!set || set.size === 0) return false;

        return active[key].some((value) => set.has(value));
    });
}

/**
 * Liest den aktuellen Stand der gesetzten Filter direkt aus dem DOM.
 * Wird für verzögerte Hide-Animationen verwendet.
 */
function getCurrentActiveFiltersFromDOM() {
    const active = {};

    document.querySelectorAll(".filter").forEach((filter) => {
        const key = filter.dataset.filter;
        const checked = filter.querySelectorAll("input:checked");

        if (checked.length) {
            active[key] = Array.from(checked).map((input) => input.value);
        }
    });

    const sortSelect = document.querySelector('select[name="sort"]');

    if (sortSelect && sortSelect.value) {
        active.sortierung = [sortSelect.value];
    }

    return active;
}

/**
 * Aktualisiert die sichtbare Anzahl aktiver Optionen pro Filter
 * und setzt die Kennzeichnung für gefilterte Bereiche.
 */
function updateFilterCounts(filters, active) {
    filters.forEach((filter) => {
        const key = filter.dataset.filter;
        const count = active[key]?.length || 0;
        const el = filter.querySelector(".count");

        filter.classList.toggle("is-filtered", count > 0);

        if (el) {
            el.textContent = count > 0 ? String(count) : "";
        }
    });
}